import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { getSelectedPart, notifyViewportBridge, setSelectedPart, setViewportBridge, subscribeViewportBridge } from '../utils/viewportBridge';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { BUILD_VOLUME, createBuildVolume } from '../utils/buildVolume';
import { disposeObject, normalizeModel } from '../utils/modelLoader';
import { computeAutoOrientation, rotatedBounds, sampleProtectedSurface, sampleVertices } from '../utils/autoOrient';
import { MODEL_LIFT, disposeSupports, generateSupports, type SupportResult } from '../utils/supportGenerator';
import { ProtectPainter, patchProtectMaterial, protectUniforms } from '../utils/protectPainter';
import { renderThumbnail } from '../utils/thumbnail';
import type { HistoryCapture, ModelSnapshot, RestoreRequest, ViewportHistoryEvent } from '../types/history';
import { easeInOutCubic, easeOutCubic, nextFrame, tween } from '../utils/tween';
import type { ViewMode } from '../types/tools';
import type { PrepareMode, PrepareRequest, PrepareStep } from '../types/prepare';
import type { ProtectCommand, ProtectCommandType, ProtectSettings } from '../types/protect';

export type ModelAppearance = 'textured' | 'material';
export type ViewportCursor = 'default' | 'grab' | 'grabbing' | 'none';

type Api = {
  setModel: (object: THREE.Object3D) => void;
  flyTo: (view: ViewMode) => void;
  setAppearance: (appearance: ModelAppearance) => void;
  runPrepare: (mode: PrepareMode) => void;
  setProtectActive: (active: boolean) => void;
  setProtectSettings: (settings: ProtectSettings) => void;
  runProtectCommand: (type: ProtectCommandType) => void;
  restore: (snapshot: ModelSnapshot, partId: string) => void;
};

type Options = {
  view: ViewMode;
  resetKey: number;
  appearance: ModelAppearance;
  prepareRequest: PrepareRequest | null;
  onPrepareProgress: (step: PrepareStep) => void;
  onPrepareComplete: (touchpoints: number) => void;
  protectActive: boolean;
  protectSettings: ProtectSettings;
  protectCommand: ProtectCommand | null;
  onProtectDirtyChange: (dirty: boolean) => void;
  onProtectAiDone: (regions: number) => void;
  brushCursorRef: RefObject<HTMLDivElement>;
  restoreRequest: RestoreRequest | null;
  onHistoryEvent: (event: ViewportHistoryEvent) => void;
};

const VIEWS: Record<ViewMode, {pos: THREE.Vector3;target: THREE.Vector3;}> = {
  perspective: { pos: new THREE.Vector3(190, 175, 330), target: new THREE.Vector3(0, 55, 0) },
  top: { pos: new THREE.Vector3(0, 480, 0.001), target: new THREE.Vector3(0, 0, 0) }
};

const resinMaterial = new THREE.MeshStandardMaterial({ color: '#e6e3dc', roughness: 0.55, metalness: 0 });
patchProtectMaterial(resinMaterial);

function applyAppearance(root: THREE.Object3D, appearance: ModelAppearance) {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    if (!mesh.userData.original) mesh.userData.original = mesh.material;
    mesh.material = appearance === 'material' ? resinMaterial : mesh.userData.original;
  });
}

export function useModelViewport(options: Options) {
  const { view, resetKey, appearance, prepareRequest, protectActive, protectSettings, protectCommand, brushCursorRef, restoreRequest } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<Api | null>(null);
  const appearanceRef = useRef(appearance);
  const callbacks = useRef(options);
  callbacks.current = options;
  const [cursor, setCursor] = useState<ViewportCursor>('default');
  const [hasDragged, setHasDragged] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;
    const cancelled = () => disposed;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.localClippingEnabled = true;
    renderer.domElement.style.display = 'block';
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#e5e8eb');
    const volume = createBuildVolume();
    scene.add(volume);
    scene.add(new THREE.HemisphereLight('#ffffff', '#b8bcc2', 1.6));
    const key = new THREE.DirectionalLight('#ffffff', 1.8);
    key.position.set(120, 260, 180);
    scene.add(key);
    const fill = new THREE.DirectionalLight('#ffffff', 0.6);
    fill.position.set(-160, 120, -120);
    scene.add(fill);

    const camera = new THREE.PerspectiveCamera(32, 1, 1, 5000);
    camera.position.copy(VIEWS.perspective.pos);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(VIEWS.perspective.target);
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.minDistance = 80;
    controls.maxDistance = 1400;

    // modelRoot (moves on X/Z when dragged) > pivot (orientation + lift) > model
    const modelRoot = new THREE.Group();
    const pivot = new THREE.Group();
    modelRoot.add(pivot);
    scene.add(modelRoot);

    let model: THREE.Object3D | null = null;
    let painter: ProtectPainter | null = null;
    let protectOn = callbacks.current.protectActive;
    let samples = new Float32Array();
    let supports: SupportResult | null = null;
    let lift = 0;
    let busy = false;
    const bounds = { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
    const clipPlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 1e4);
    const clones = new Map<string, THREE.Object3D>();
    /** Each duplicate owns its geometry, so it gets its own independent protection painter. */
    type ClonePart = {
      painter: ProtectPainter;
      model: THREE.Object3D;
      pivot: THREE.Object3D;
      root: THREE.Object3D;
      samples: Float32Array;
      lift: number;
      supports: SupportResult | null;
    };
    const clonePainters = new Map<string, ClonePart>();
    let cloneSeq = 0;
    const clearClones = () => {
      clonePainters.forEach((c) => {
        if (c.supports) {
          c.root.remove(c.supports.group);
          disposeSupports(c.supports.group);
        }
      });
      clones.forEach((c) => {
        scene.remove(c);
        c.traverse((o) => (o as THREE.Mesh).isMesh && (o as THREE.Mesh).geometry.dispose());
      });
      clonePainters.forEach(({ painter: p }) => p.dispose());
      clones.clear();
      clonePainters.clear();
    };
    // ---- Selection frame: tightest axis-aligned box around the selected model ----
    const selBox = new THREE.Box3();
    const localSel = new THREE.Box3();
    const selHelper = new THREE.Box3Helper(selBox, new THREE.Color('#1a73e8'));
    const selMaterial = selHelper.material as THREE.LineBasicMaterial;
    selMaterial.depthTest = false;
    selMaterial.transparent = true;
    selMaterial.opacity = 0.95;
    selHelper.renderOrder = 998;
    selHelper.visible = false;
    scene.add(selHelper);
    let selTarget: {root: THREE.Object3D;shape: THREE.Object3D;} | null = null;
    let selDirty = true;
    const resolveSelection = (id: string | null) => {
      if (!id || !model) return null;
      if (id === 'main') return { root: modelRoot as THREE.Object3D, shape: model };
      const c = clonePainters.get(id);
      return c ? { root: c.root, shape: c.model } : null;
    };
    const syncSelection = () => {
      const next = resolveSelection(getSelectedPart());
      if (next?.root !== selTarget?.root) {
        selTarget = next;
        selDirty = true;
      }
    };
    const updateSelectionFrame = () => {
      if (!selTarget || !selTarget.root.visible) {
        selHelper.visible = false;
        return;
      }
      if (selDirty) {
        selTarget.root.updateMatrixWorld(true);
        localSel.setFromObject(selTarget.shape, true).translate(selTarget.root.position.clone().negate());
        selDirty = false;
      }
      selBox.copy(localSel).translate(selTarget.root.position);
      selHelper.visible = !localSel.isEmpty();
    };
    const unsubscribeSelection = subscribeViewportBridge(syncSelection);

    const allPainters = () => [...(painter ? [painter] : []), ...[...clonePainters.values()].map((c) => c.painter)];
    const anyDirty = () => allPainters().some((p) => p.isDirty());

    const updateBounds = () => {
      modelRoot.updateMatrixWorld(true);
      const b = new THREE.Box3().setFromObject(modelRoot);
      if (b.isEmpty()) return;
      bounds.minX = b.min.x - modelRoot.position.x;
      bounds.maxX = b.max.x - modelRoot.position.x;
      bounds.minZ = b.min.z - modelRoot.position.z;
      bounds.maxZ = b.max.z - modelRoot.position.z;
    };

    /** Centres the rotated model over modelRoot and rests its lowest point at `height`. */
    const place = (height: number) => {
      if (!samples.length) return;
      const { min, max } = rotatedBounds(samples, pivot.quaternion);
      pivot.position.set(-(min.x + max.x) / 2, height - min.y, -(min.z + max.z) / 2);
      if (selTarget?.root === modelRoot) selDirty = true;
    };

    /** Orientation and auto-supports only respect protection the user has saved. */
    const withSavedProtection = <T>(fn: () => T): T => painter ? painter.withSaved(fn) : fn();

    const removeSupports = () => {
      if (!supports) return;
      modelRoot.remove(supports.group);
      disposeSupports(supports.group);
      supports = null;
    };

    /** Snapshot of the committed state plus a rendered preview, for the history timeline. */
    /** Live state of a model in the build: the original ('main') or a duplicate. */
    const describePart = (partId: string) => {
      const c = partId !== 'main' ? clonePainters.get(partId) : undefined;
      if (c) return { root: c.root, pivot: c.pivot, lift: c.lift, supports: c.supports, painter: c.painter as ProtectPainter | null };
      return { root: modelRoot as THREE.Object3D, pivot: pivot as THREE.Object3D, lift, supports, painter };
    };

    const capture = (showProtection = false, partId = 'main'): HistoryCapture => {
      const s = describePart(partId);
      const snapshot: ModelSnapshot = {
        quaternion: s.pivot.quaternion.toArray(),
        lift: s.lift,
        position: s.root.position.toArray(),
        protect: s.painter?.snapshot() ?? [],
        touchpoints: s.supports?.touchpoints ?? 0,
        hasSupports: !!s.supports
      };
      const prevShow = protectUniforms.uProtectShow.value;
      protectUniforms.uProtectShow.value = showProtection ? 1 : prevShow;
      s.root.updateMatrixWorld(true);
      const hide = [volume, selHelper, ...allPainters().map((p) => p.cursorObject)];
      const thumbnail = renderThumbnail(renderer, scene, s.root, hide);
      protectUniforms.uProtectShow.value = prevShow;
      return { snapshot, thumbnail };
    };

    const emit = (event: Omit<ViewportHistoryEvent, 'capture' | 'partId'>, showProtection = false, partId = 'main') => {
      updateBounds();
      callbacks.current.onHistoryEvent({ ...event, partId, capture: capture(showProtection, partId) });
    };

    const animatePose = async (targetQ: THREE.Quaternion, targetLift: number, duration: number) => {
      const fromQ = pivot.quaternion.clone();
      const fromLift = lift;
      await tween(
        duration,
        (k) => {
          const e = easeInOutCubic(k);
          pivot.quaternion.slerpQuaternions(fromQ, targetQ, e);
          place(fromLift + (targetLift - fromLift) * e);
        },
        cancelled
      );
      lift = targetLift;
    };

    let camTween: {fromPos: THREE.Vector3;toPos: THREE.Vector3;fromT: THREE.Vector3;toT: THREE.Vector3;start: number;} | null = null;
    const flyTo = (v: ViewMode) => {
      camTween = { fromPos: camera.position.clone(), toPos: VIEWS[v].pos.clone(), fromT: controls.target.clone(), toT: VIEWS[v].target.clone(), start: performance.now() };
    };

    // ---- Prepare a duplicate: same pipeline as the original, applied to its own pivot ----
    const placePart = (p: ClonePart, height: number) => {
      const { min, max } = rotatedBounds(p.samples, p.pivot.quaternion);
      p.pivot.position.set(-(min.x + max.x) / 2, height - min.y, -(min.z + max.z) / 2);
      if (selTarget?.root === p.root) selDirty = true;
    };

    const animatePartPose = async (p: ClonePart, targetQ: THREE.Quaternion, targetLift: number, duration: number) => {
      const fromQ = p.pivot.quaternion.clone();
      const fromLift = p.lift;
      await tween(
        duration,
        (k) => {
          const e = easeInOutCubic(k);
          p.pivot.quaternion.slerpQuaternions(fromQ, targetQ, e);
          placePart(p, fromLift + (targetLift - fromLift) * e);
        },
        cancelled
      );
      p.lift = targetLift;
    };

    const removePartSupports = (p: ClonePart) => {
      if (!p.supports) return;
      p.root.remove(p.supports.group);
      disposeSupports(p.supports.group);
      p.supports = null;
    };

    /** Grows supports on a duplicate with its own clip plane so other models' supports aren't affected. */
    const growPartSupports = async (p: ClonePart, duration: number) => {
      const plane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
      const result = p.painter.withSaved(() => generateSupports(p.model, p.root, plane));
      if (!result) return null;
      p.supports = result;
      p.root.add(result.group);
      await tween(duration, (k) => plane.constant = easeOutCubic(k) * (result.height + 1), cancelled);
      plane.constant = 1e4;
      return result;
    };

    const restorePart = async (p: ClonePart, snap: ModelSnapshot) => {
      busy = true;
      removePartSupports(p);
      p.root.position.fromArray(snap.position);
      p.painter.load(snap.protect);
      callbacks.current.onProtectDirtyChange(anyDirty());
      try {
        await animatePartPose(p, new THREE.Quaternion().fromArray(snap.quaternion), snap.lift, 320);
        if (disposed || !snap.hasSupports) return;
        await nextFrame();
        await growPartSupports(p, 500);
      } finally {
        busy = false;
      }
    };

    const runPreparePart = async (partId: string, p: ClonePart, mode: PrepareMode) => {
      busy = true;
      removePartSupports(p);
      try {
        if (mode === 'clear') {
          await animatePartPose(p, p.pivot.quaternion.clone(), 0, 260);
          if (!disposed) emit({ type: 'cleared' }, false, partId);
          return;
        }
        if (mode === 'full' || mode === 'orient') {
          callbacks.current.onPrepareProgress('orienting');
          const surface = p.painter.withSaved(() => sampleProtectedSurface(p.model, p.pivot));
          const result = computeAutoOrientation(p.samples, surface);
          await animatePartPose(p, result.quaternion, mode === 'orient' ? 0 : MODEL_LIFT, 900);
          if (!disposed) emit({ type: 'oriented', protectedExposure: result.protectedExposure }, false, partId);
          if (mode === 'orient') {
            callbacks.current.onPrepareComplete(0);
            return;
          }
        } else {
          await animatePartPose(p, p.pivot.quaternion.clone(), MODEL_LIFT, 350);
        }
        if (disposed) return;
        callbacks.current.onPrepareProgress('supporting');
        await nextFrame();
        const result = await growPartSupports(p, 1300);
        if (!result) {
          callbacks.current.onPrepareComplete(0);
          return;
        }
        callbacks.current.onPrepareComplete(result.touchpoints);
        if (!disposed) emit({ type: 'supports', mode, touchpoints: result.touchpoints, protectedAvoided: result.protectedAvoided }, false, partId);
      } finally {
        busy = false;
      }
    };

    const runPrepare = async (mode: PrepareMode) => {
      if (busy) return;
      const selected = getSelectedPart();
      const clonePart = selected && selected !== 'main' ? clonePainters.get(selected) : undefined;
      if (clonePart && model && selected) {
        await runPreparePart(selected, clonePart, mode);
        return;
      }
      if (!model) {
        if (mode !== 'clear') callbacks.current.onPrepareComplete(0);
        return;
      }
      busy = true;
      removeSupports();
      try {
        if (mode === 'clear') {
          await animatePose(pivot.quaternion.clone(), 0, 260);
          if (!disposed) emit({ type: 'cleared' });
          return;
        }
        if (mode === 'full' || mode === 'orient') {
          callbacks.current.onPrepareProgress('orienting');
          const m = model;
          const surface = withSavedProtection(() => sampleProtectedSurface(m, pivot));
          const result = computeAutoOrientation(samples, surface);
          await animatePose(result.quaternion, mode === 'orient' ? 0 : MODEL_LIFT, 900);
          if (!disposed) emit({ type: 'oriented', protectedExposure: result.protectedExposure });
          if (mode === 'orient') {
            callbacks.current.onPrepareComplete(0);
            return;
          }
        } else {
          await animatePose(pivot.quaternion.clone(), MODEL_LIFT, 350);
        }
        if (disposed) return;

        callbacks.current.onPrepareProgress('supporting');
        await nextFrame();
        const target = model;
        if (!target) return;
        const result = withSavedProtection(() => generateSupports(target, modelRoot, clipPlane));
        if (!result) {
          callbacks.current.onPrepareComplete(0);
          return;
        }
        supports = result;
        clipPlane.constant = 0;
        modelRoot.add(result.group);
        await tween(1300, (k) => clipPlane.constant = easeOutCubic(k) * (result.height + 1), cancelled);
        clipPlane.constant = 1e4;

        if (mode === 'full') {
          callbacks.current.onPrepareProgress('layout');
          const from = modelRoot.position.clone();
          const to = new THREE.Vector3(0, 0, 0);
          await tween(450, (k) => modelRoot.position.lerpVectors(from, to, easeInOutCubic(k)), cancelled);
        }
        callbacks.current.onPrepareComplete(result.touchpoints);
        if (!disposed) emit({ type: 'supports', mode, touchpoints: result.touchpoints, protectedAvoided: result.protectedAvoided });
      } finally {
        busy = false;
        updateBounds();
      }
    };

    // ---- Pointer interaction: drag the model, or paint protection when Protect is open ----
    const raycaster = new THREE.Raycaster();
    raycaster.firstHitOnly = true;
    const pointer = new THREE.Vector2();
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const hit = new THREE.Vector3();
    const offset = new THREE.Vector3();
    let dragging = false;
    let dragMoved = false;
    let painting = false;

    const updatePointer = (e: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.set((e.clientX - rect.left) / rect.width * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(pointer, camera);
    };
    /** The draggable group (original or a duplicate) under the pointer, nearest first. */
    const pickDraggable = (): THREE.Object3D | null => {
      if (!model) return null;
      const targets = [modelRoot, ...clones.values()].filter((t) => t.visible);
      const h = raycaster.intersectObjects(targets, true)[0];
      if (!h) return null;
      let o: THREE.Object3D | null = h.object;
      while (o && !targets.includes(o)) o = o.parent;
      return o;
    };
    const hitsModel = () => !!pickDraggable();
    let dragTarget: THREE.Object3D = modelRoot;
    const dragBounds = { minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
    /** Nearest paintable surface under the pointer — on the original or any visible duplicate. */
    const paintHit = (): {painter: ProtectPainter;hit: THREE.Intersection;} | null => {
      if (!model || !painter) return null;
      const owners: {root: THREE.Object3D;painter: ProtectPainter;}[] = [];
      if (modelRoot.visible) owners.push({ root: model, painter });
      clonePainters.forEach((c, id) => clones.get(id)?.visible && owners.push({ root: c.model, painter: c.painter }));
      const hit = raycaster.intersectObjects(owners.map((o) => o.root), true)[0];
      if (!hit) return null;
      let o: THREE.Object3D | null = hit.object;
      while (o) {
        const owner = owners.find((w) => w.root === o);
        if (owner) return { painter: owner.painter, hit };
        o = o.parent;
      }
      return null;
    };
    let strokePainter: ProtectPainter | null = null;
    const hoverPaint = (target: {painter: ProtectPainter;hit: THREE.Intersection;} | null) => {
      allPainters().forEach((p) => p.hover(target && target.painter === p ? target.hit : null));
    };

    const moveBrushCursor = (e: PointerEvent | null) => {
      const el = callbacks.current.brushCursorRef.current;
      if (!el) return;
      if (!e) {
        el.style.display = 'none';
        return;
      }
      const rect = container.getBoundingClientRect();
      el.style.display = 'block';
      el.style.transform = `translate(${e.clientX - rect.left}px, ${e.clientY - rect.top}px)`;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 || busy) return;
      updatePointer(e);
      if (protectOn && painter) {
        const t = paintHit();
        if (!t) return;
        painting = true;
        controls.enabled = false;
        strokePainter = t.painter;
        t.painter.strokeStart(t.hit);
        return;
      }
      const target = pickDraggable();
      if (!target) {
        bgDown = { x: e.clientX, y: e.clientY };
        return;
      }
      let partId = 'main';
      clones.forEach((c, id) => {
        if (c === target) partId = id;
      });
      setSelectedPart(partId);
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      dragTarget = target;
      if (target === modelRoot) {
        updateBounds();
        Object.assign(dragBounds, bounds);
      } else {
        target.updateMatrixWorld(true);
        const b = new THREE.Box3().setFromObject(target);
        dragBounds.minX = b.min.x - target.position.x;
        dragBounds.maxX = b.max.x - target.position.x;
        dragBounds.minZ = b.min.z - target.position.z;
        dragBounds.maxZ = b.max.z - target.position.z;
      }
      dragging = true;
      dragMoved = false;
      controls.enabled = false;
      offset.copy(target.position).sub(hit);
      setCursor('grabbing');
    };

    const clampAxis = (value: number, lo: number, hi: number) => lo > hi ? (lo + hi) / 2 : THREE.MathUtils.clamp(value, lo, hi);

    const onPointerMove = (e: PointerEvent) => {
      if (protectOn && painter) {
        if (e.target !== renderer.domElement && !painting) {
          hoverPaint(null);
          moveBrushCursor(null);
          return;
        }
        updatePointer(e);
        let t = paintHit();
        // Keep a stroke on the model it started on.
        if (painting && t && t.painter !== strokePainter) t = null;
        hoverPaint(t);
        moveBrushCursor(t ? e : null);
        setCursor(t ? 'none' : 'default');
        if (painting && t) t.painter.strokeTo(t.hit);
        return;
      }
      if (e.target !== renderer.domElement && !dragging) return;
      updatePointer(e);
      if (dragging) {
        if (!raycaster.ray.intersectPlane(plane, hit)) return;
        const hw = BUILD_VOLUME.width / 2;
        const hd = BUILD_VOLUME.depth / 2;
        dragTarget.position.x = clampAxis(hit.x + offset.x, -hw - dragBounds.minX, hw - dragBounds.maxX);
        dragTarget.position.z = clampAxis(hit.z + offset.z, -hd - dragBounds.minZ, hd - dragBounds.maxZ);
        dragMoved = true;
        setHasDragged(true);
        return;
      }
      if (e.buttons === 0) setCursor(!busy && hitsModel() ? 'grab' : 'default');
    };

    let bgDown: {x: number;y: number;} | null = null;

    const onPointerUp = (e: PointerEvent) => {
      // A click (not an orbit drag) on empty space clears the selection.
      if (bgDown) {
        if (Math.hypot(e.clientX - bgDown.x, e.clientY - bgDown.y) < 4) setSelectedPart(null);
        bgDown = null;
      }
      if (painting) {
        painting = false;
        controls.enabled = true;
        strokePainter?.strokeEnd();
        strokePainter = null;
        callbacks.current.onProtectDirtyChange(anyDirty());
        return;
      }
      if (!dragging) return;
      dragging = false;
      controls.enabled = true;
      setCursor('grab');
      if (dragMoved) {
        let partId = 'main';
        clones.forEach((c, id) => {
          if (c === dragTarget) partId = id;
        });
        emit({ type: 'moved' }, false, partId);
      }
    };

    const onPointerLeave = () => {
      if (painting) return;
      hoverPaint(null);
      moveBrushCursor(null);
    };

    container.addEventListener('pointerdown', onPointerDown, { capture: true });
    container.addEventListener('pointerleave', onPointerLeave);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    const resize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();

    let frame = 0;
    let lastTime = performance.now();
    const loop = () => {
      frame = requestAnimationFrame(loop);
      const now = performance.now();
      const dt = Math.min(0.1, (now - lastTime) / 1000);
      lastTime = now;
      if (camTween) {
        const t = Math.min(1, (now - camTween.start) / 280);
        const k = easeOutCubic(t);
        camera.position.lerpVectors(camTween.fromPos, camTween.toPos, k);
        controls.target.lerpVectors(camTween.fromT, camTween.toT, k);
        if (t === 1) camTween = null;
      }
      allPainters().forEach((p) => p.update(dt));
      updateSelectionFrame();
      controls.update();
      renderer.render(scene, camera);
    };
    loop();

    apiRef.current = {
      setModel: (object) => {
        removeSupports();
        painter?.dispose();
        if (model) {
          pivot.remove(model);
          disposeObject(model);
        }
        pivot.quaternion.identity();
        pivot.position.set(0, 0, 0);
        modelRoot.position.set(0, 0, 0);
        lift = 0;
        clearClones();
        normalizeModel(object);
        object.userData.importScale = object.scale.x;
        applyAppearance(object, appearanceRef.current);
        object.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (mesh.isMesh && !mesh.geometry.boundsTree) mesh.geometry.computeBoundsTree();
        });
        pivot.add(object);
        model = object;
        samples = sampleVertices(object, pivot);
        place(0);
        updateBounds();
        painter = new ProtectPainter(object, scene);
        painter.configure(callbacks.current.protectSettings);
        painter.setVisible(protectOn);
        emit({ type: 'imported' });
        selDirty = true;
        notifyViewportBridge();
      },
      restore: (snap, partId) => {
        if (busy || !model) return;
        if (partId !== 'main') {
          const c = clonePainters.get(partId);
          if (c) void restorePart(c, snap);
          return;
        }
        busy = true;
        removeSupports();
        modelRoot.position.fromArray(snap.position);
        painter?.load(snap.protect);
        callbacks.current.onProtectDirtyChange(false);
        const targetQ = new THREE.Quaternion().fromArray(snap.quaternion);
        void (async () => {
          try {
            await animatePose(targetQ, snap.lift, 320);
            if (disposed || !model || !snap.hasSupports) return;
            await nextFrame();
            const target = model;
            const result = withSavedProtection(() => generateSupports(target, modelRoot, clipPlane));
            if (!result) return;
            supports = result;
            clipPlane.constant = 0;
            modelRoot.add(result.group);
            await tween(500, (k) => clipPlane.constant = easeOutCubic(k) * (result.height + 1), cancelled);
            clipPlane.constant = 1e4;
          } finally {
            busy = false;
            updateBounds();
          }
        })();
      },
      flyTo,
      setAppearance: (a) => {
        if (model) applyAppearance(model, a);
        clonePainters.forEach((c) => applyAppearance(c.model, a));
      },
      runPrepare: (mode) => void runPrepare(mode),
      setProtectActive: (active) => {
        protectOn = active;
        allPainters().forEach((p) => p.setVisible(active));
        if (!active) {
          painting = false;
          controls.enabled = true;
          moveBrushCursor(null);
          setCursor('default');
        }
      },
      setProtectSettings: (s) => allPainters().forEach((p) => p.configure(s)),
      runProtectCommand: (type) => {
        const painters = allPainters();
        if (painters.length === 0) {
          if (type === 'ai') callbacks.current.onProtectAiDone(0);
          return;
        }
        if (type === 'save') {
          // Each model that changed gets its own "Protected surfaces" step in its timeline.
          const parts: [string, ProtectPainter][] = [...(painter ? [['main', painter] as [string, ProtectPainter]] : []), ...[...clonePainters.entries()].map(([id, c]) => [id, c.painter] as [string, ProtectPainter])];
          parts.forEach(([id, p]) => {
            const changed = p.isDirty();
            p.save();
            if (changed) emit({ type: 'protected' }, true, id);
          });
        }
        if (type === 'revert') painters.forEach((p) => p.revert());
        if (type === 'clear') painters.forEach((p) => p.clearAll());
        if (type === 'ai') {
          void Promise.all(painters.map((p) => p.aiSelect(cancelled))).then((counts) => {
            if (disposed) return;
            callbacks.current.onProtectAiDone(counts[0] ?? 0);
            callbacks.current.onProtectDirtyChange(anyDirty());
          });
          return;
        }
        callbacks.current.onProtectDirtyChange(anyDirty());
      }
    };

    setViewportBridge({
      getModel: () => model,
      getOrientation: () => pivot.quaternion.clone(),
      duplicate: () => {
        if (!model) return null;
        updateBounds();
        cloneSeq++;
        const n = clones.size + 1;
        const step = bounds.maxX - bounds.minX + 6;
        const copy = new THREE.Group();
        copy.position.copy(modelRoot.position);
        copy.position.x += (n % 2 === 1 ? 1 : -1) * Math.ceil(n / 2) * step;
        const pivotCopy = pivot.clone(true);
        const modelCopy = pivotCopy.children[pivot.children.indexOf(model)];
        // Give the copy its own geometry (and protect attribute) so painting it doesn't paint the original.
        const sourceMeshes: THREE.Mesh[] = [];
        model.traverse((o) => (o as THREE.Mesh).isMesh && sourceMeshes.push(o as THREE.Mesh));
        let mi = 0;
        modelCopy.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (!mesh.isMesh) return;
          const src = sourceMeshes[mi++];
          mesh.geometry = mesh.geometry.clone();
          mesh.geometry.computeBoundsTree();
          mesh.material = src.material;
          if (src.userData.original) mesh.userData.original = src.userData.original;
        });
        copy.add(pivotCopy);
        scene.add(copy);
        const id = `copy-${cloneSeq}`;
        clones.set(id, copy);
        const copyPainter = new ProtectPainter(modelCopy, scene);
        copyPainter.configure(callbacks.current.protectSettings);
        copyPainter.setVisible(protectOn);
        clonePainters.set(id, {
          painter: copyPainter,
          model: modelCopy,
          pivot: pivotCopy,
          root: copy,
          samples: sampleVertices(modelCopy, pivotCopy),
          lift,
          supports: null
        });
        emit({ type: 'duplicated' }, false, id);
        return id;
      },
      setVisible: (id, visible) => {
        const target = id === 'main' ? modelRoot : clones.get(id);
        if (target) target.visible = visible;
      }
    });

    return () => {
      disposed = true;
      setViewportBridge(null);
      unsubscribeSelection();
      scene.remove(selHelper);
      selHelper.dispose();
      clearClones();
      cancelAnimationFrame(frame);
      observer.disconnect();
      container.removeEventListener('pointerdown', onPointerDown, { capture: true });
      container.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      removeSupports();
      painter?.dispose();
      if (model) disposeObject(model);
      controls.dispose();
      renderer.dispose();
      container.removeChild(renderer.domElement);
      apiRef.current = null;
    };
  }, []);

  useEffect(() => {
    apiRef.current?.flyTo(view);
  }, [view, resetKey]);

  useEffect(() => {
    appearanceRef.current = appearance;
    apiRef.current?.setAppearance(appearance);
  }, [appearance]);

  useEffect(() => {
    if (prepareRequest) apiRef.current?.runPrepare(prepareRequest.mode);
  }, [prepareRequest]);

  useEffect(() => {
    apiRef.current?.setProtectActive(protectActive);
  }, [protectActive]);

  useEffect(() => {
    apiRef.current?.setProtectSettings(protectSettings);
  }, [protectSettings]);

  useEffect(() => {
    if (protectCommand) apiRef.current?.runProtectCommand(protectCommand.type);
  }, [protectCommand]);

  useEffect(() => {
    if (restoreRequest) apiRef.current?.restore(restoreRequest.snapshot, restoreRequest.partId);
  }, [restoreRequest]);

  useEffect(() => {
    if (brushCursorRef.current) brushCursorRef.current.style.display = 'none';
  }, [brushCursorRef]);

  const setModel = useCallback((object: THREE.Object3D) => apiRef.current?.setModel(object), []);

  return { containerRef, setModel, cursor, hasDragged };
}
